import {
  DataManagerPaginatorPosition,
  DataTableDateCell,
  FormDefaultAttributes,
  type DataTableItem,
  type FormConfig,
  type PartialDataManagerConfig,
  type PartialDataManagerPaginatorConfig,
  type PartialDataTableConfig,
  type SelectConfig,
  type TextConfig
} from '@common';
import {
  DecisionSupportDataTableStatusCell,
  DecisionSupportDataTableParcelsCell,
  DecisionSupportDataTableCropsCell
} from '../components';
import { markRaw } from 'vue';
import type { DecisionSupportItem } from '../models';
import { DecisionSupportStatus } from '../enums';

export const DECISION_SUPPORT_TABLE_CONFIG: PartialDataTableConfig = {
  needTableHeader: true,
  showViewModes: false,
  fixedHeader: true,
  showIndexColumn: false,
  mobileBreakpoint: 900,
  clickableItems: true,
  height: '100%',
  hover: true,
  columns: [
    {
      name: 'name',
      label: 'decisionSupport.label.name',
      translateLabel: true
    },
    {
      name: 'crop',
      label: 'decisionSupport.label.crop',
      translateLabel: true,
      cellConfig: {
        emptyValue: '-'
      },
      cellComponent: markRaw(DecisionSupportDataTableCropsCell)
    },
    {
      name: 'parcelNames',
      label: 'decisionSupport.label.parcelNames',
      translateLabel: true,
      cellComponent: markRaw(DecisionSupportDataTableParcelsCell)
    },
    {
      name: 'createdAt',
      label: 'decisionSupport.label.createdAt',
      translateLabel: true,
      cellConfig: {
        dateFormat: 'yyyy.MM.dd'
      },
      cellComponent: markRaw(DataTableDateCell)
    },
    {
      name: 'status',
      label: 'decisionSupport.label.decisionSupportStatus',
      translateLabel: true,
      cellConfig: {
        emptyValue: '-'
      },
      cellComponent: markRaw(DecisionSupportDataTableStatusCell)
    }
  ],
  actions: {
    global: [],
    row: [
      {
        action: 'details',
        actionConfig: {
          label: 'button.details',
          translateLabel: true,
          icon: 'mdi-eye',
          size: 'small',
          variant: 'flat'
        },
        disabledCondition: (item: DataTableItem<DecisionSupportItem>) =>
          [DecisionSupportStatus.ERROR, DecisionSupportStatus.GENERATING].includes(item.data.status)
      },
      {
        action: 'copy',
        actionConfig: {
          label: 'button.copy',
          translateLabel: true,
          icon: 'mdi-content-copy',
          size: 'small',
          variant: 'flat'
        },
        disabledCondition: (item: DataTableItem<DecisionSupportItem>) =>
          item.data.status === DecisionSupportStatus.GENERATING
      },
      {
        action: 'delete',
        actionConfig: {
          label: 'button.delete',
          translateLabel: true,
          icon: 'mdi-delete',
          size: 'small',
          variant: 'flat',
          iconColor: 'error'
        },
        disabledCondition: (item: DataTableItem<DecisionSupportItem>) =>
          item.data.status === DecisionSupportStatus.GENERATING
      }
    ]
  }
};

export const DECISION_SUPPORT_FILTER_FORM_CONFIG: FormConfig = {
  name: 'decisionSupportTableFiltersForm',
  attributes: {
    ...FormDefaultAttributes.getFormDefaultAttributes(),
    class: 'flex-grow-1 z-5'
  },
  settings: {},
  children: [
    {
      name: 'searchText',
      component: 'text',
      attributes: {
        type: 'text',
        label: 'filter.search',
        hideDetails: true
      },
      settings: {
        grid: { cols: 12, md: 4 },
        needTranslate: true
      }
    } as TextConfig,
    {
      component: 'select',
      name: 'crop',
      attributes: {
        label: 'decisionSupport.label.crop',
        hideDetails: true,
        multiple: false,
        itemTitle: 'name',
        itemValue: 'code',
        clearable: true
      },
      settings: {
        needTranslate: true,
        needItemTranslate: false,
        grid: { cols: 12, md: 4 }
      }
    } as SelectConfig
  ]
};

export const DECISION_SUPPORT_PAGINATOR_CONFIG: PartialDataManagerPaginatorConfig = {
  totalVisible: 7,
  itemsPerPage: [20, 50, 100],
  defaultPageSize: 50,
  rounded: true,
  position: DataManagerPaginatorPosition.BOTTOM
};

export const DECISION_SUPPORT_DATA_MANAGER_CONFIG: PartialDataManagerConfig = {
  name: 'decisionSupportDataManager',
  dataTableConfig: DECISION_SUPPORT_TABLE_CONFIG,
  filters: {
    filterDelay: 500
  },
  paginator: DECISION_SUPPORT_PAGINATOR_CONFIG
};
